import {
  BusinessDetailsType,
  ContactDetailsType,
  EligibilityType,
  FinancePurchaseType,
  HouseholdType,
  IdentityType,
  IncomeReviewFieldsType,
  IncomeVerificationType,
  OwnershipDetailsType,
  RepaymentAccountType,
  SummaryType,
} from './';

export enum AddressState {
  ACT = 'ACT',
  NSW = 'NSW',
  NT = 'NT',
  QLD = 'QLD',
  SA = 'SA',
  TAS = 'TAS',
  VIC = 'VIC',
  WA = 'WA',
}

export type AddressType = {
  unitNumber?: string;
  streetNumber: string;
  streetName: string;
  suburb: string;
  state: AddressState;
  postcode: string;
  dwellingType?: string;
  occupancyStatus?: string;
};

export type repaymentOptions = {
  term: number;
  amount: number;
  termNumberOfPayments: number;
  paymentRegularFirstDate: string;
  paymentCountRegular: number;
  interestCycle: string;
  paymentCycle: string;
  totalInterest: number;
  totalPayments: number;
  dateMaturity: string;
  interestRate: number;
};

export enum ApplicantStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
  Specified = 'SPECIFIED',
  Declined = 'DECLINED',
  Accepted = 'ACCEPTED',
  Approved = 'APPROVED',
}

export enum ApplicationFormStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
  Approved = 'APPROVED',
  Declined = 'DECLINED',
  Withdrawn = 'WITHDRAWN',
}

export enum ApplicationFormSubStatus {
  PendingAcceptance = 'PEND.ACPT',
  Accepted = 'ACCEPTED',
  Approved = 'APPROVED',
  PendingAdditionalIncomeDocuments = 'PEND.ADDIN',
  HoldJoint = 'HOLD.JOINT',
}

export enum MedicareCardColour {
  Blue = 'Blue',
  Green = 'Green',
  Yellow = 'Yellow',
}

export enum MedicareType {
  Yellow = 'YELLOW',
  Green = 'GREEN',
  Blue = 'BLUE',
}

export enum EmploymentType {
  SELF_EMPLOYED = 'self-employed',
  EMPLOYED = 'employed',
  PENSIONER = 'pensioner',
  OTHER = 'other',
}

export enum PensionType {
  DISABILITY = 'DISABILITY',
  PENSIONER = 'PENSIONER',
  SELF_FUNDED_RETIREE = 'SELF_FUNDED_RETIREE',
  VETERAN = 'VETERAN',
}

export enum Period {
  Fortnightly = 'Fortnightly',
  Monthly = 'Monthly',
  Weekly = 'Weekly',
  Annually = 'Annually',
}

export enum IdentificationType {
  DRIVING_LICENSE = 'DRIVING_LICENSE',
  MEDICARE = 'MEDICARE',
  PASSPORT = 'PASSPORT',
  PHOTO_ID = 'PHOTO_ID',
}

export enum ApplicantType {
  Primary = 'Primary',
  Joint = 'Joint',
}

export enum ApplicationStep {
  AddJointApplicant = 'AddJointApplicant',
  BeneficialOwner = 'BeneficialOwner',
  BusinessDetails = 'BusinessDetails',
  CreateApplication = 'CreateApplication',
  GuarantorDetails = 'GuarantorDetails',
  HouseholdDetails = 'HouseholdDetails',
  Identification = 'Identification',
  IncomeVerification = 'IncomeVerification',
  JointEligibility = 'JointEligibility',
  OwnershipDetails = 'OwnershipDetails',
  PaymentDetails = 'PaymentDetails',
  SubmitApplication = 'SubmitApplication',
}

export type ApplicationFormType = {
  Finance: FinancePurchaseType;
  Eligibility: EligibilityType;
  Identification: IdentityType;
  IncomeVerification: IncomeVerificationType;
  HouseholdDetails: HouseholdType;
  PaymentDetails: RepaymentAccountType;
  SubmitApplication: SummaryType;
  ContactDetails: ContactDetailsType;
  BusinessDetails: BusinessDetailsType;
  OwnershipDetails: OwnershipDetailsType;
  IncomeReview: IncomeReviewFieldsType;
};

export type ApplicationFormValues = ApplicationFormType[keyof ApplicationFormType];

export enum EnterpriseStructure {
  Company = 'Company',
  Partnership = 'Partnership',
  Trust = 'Trust',
  SoleTrader = 'Sole Trader',
  IncorporatedAssociation = 'Incorporated Association',
  UnincorporatedAssociation = 'Unincorporated Association',
  ProprietaryLimitedCompany = 'Proprietary Limited Company',
}

const EXTENDED_STRUCTURES: EnterpriseStructure[] = [
  EnterpriseStructure.ProprietaryLimitedCompany,
  EnterpriseStructure.Partnership,
  EnterpriseStructure.Trust,
  EnterpriseStructure.SoleTrader,
];

export const isExtendedStructure = (structure: EnterpriseStructure): boolean => EXTENDED_STRUCTURES.includes(structure);

export enum OwnershipType {
  Proprietary = 'Proprietary',
  Public = 'Public',
  Individual = 'Individual',
  Corporate = 'Corporate',
}

export enum CustomerType {
  Business = 'Business',
  Residential = 'Residential',
}

export enum LoanType {
  Loan = 'Loan',
  PaymentPlan = 'PaymentPlan',
}

export enum VerificationType {
  SmsEmail = 'SmsEmail',
  Sms = 'Sms',
  Email = 'Email',
}

// Application document entity type
// use to define which entity type the uploaded document belongs to
export enum ApplicationEntityType {
  Application = 'Application',
  Primary = 'Primary',
  Joint = 'Joint',
}

export enum ApplicationDocumentType {
  Identification = 'Identification',
  IncomeVerification = 'IncomeVerification',
  Others = 'Others',
}

export enum DocumentStatus {
  New = 'New',
  Submitted = 'Submitted',
  Deleted = 'Deleted',
  Uploaded = 'Uploaded',
}

export enum DocumentRequestStatus {
  New = 'New',
  Submitted = 'Submitted',
  Expired = 'Expired',
}

export enum DocumentS3FileStatus {
  Exists = 'Exists',
  Deleted = 'Deleted',
}

export enum PaymentFunds {
  CurrentSavings = 'Current savings',
  MortgageRedraw = 'Mortgage redraw',
  FutureEarnings = 'Future earnings',
}

export enum RepaymentType {
  DirectDebit = 'DEBIT',
  CreditCard = 'CARD',
}

export enum IncomeCategory {
  INCOME_SALARY = 'INCOME_SALARY',
  INCOME_INVESTMENTS = 'INCOME_INVESTMENTS',
  INCOME_CHILD_SUPPORT = 'INCOME_CHILD_SUPPORT',
  INCOME_RENTAL = 'INCOME_RENTAL',
  INCOME_GOVERNMENT_BENEFITS = 'INCOME_GOVERNMENT_BENEFITS',
}

export enum PaymentFrequency {
  WEEKLY = 'weekly',
  FORTNIGHTLY = 'fortnightly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  IRREGULAR = 'irregular',
  UNDETERMINED = 'undetermined',
}

export enum OpenBankingFeedBack {
  PROBLEM_LOGGING_IN = 'PROBLEM_LOGGING_IN',
  PREFER_MANUAL = 'PREFER_MANUAL',
  TOO_COMPLICATED = 'TOO_COMPLICATED',
  OTHER = 'OTHER',
}

export enum FeatureFlags {
  OpenBankingMultipleConsents = 'open-banking-multiple-consents',
}

export enum IncomeVerificationInput {
  Failed = 'Failed',
}

export enum IncomeVerificationStatus {
  Pending = 'Pending',
  Passed = 'Passed',
  Failed = 'Failed',
}
